import React, { useEffect, useMemo, useState } from "react";
import { FreeTS } from "../../api/template_shift";
import { AddForeignShift } from "../../api/shift";
import { GetReportCHK } from "../../api/report";
import { MNGLevel, MngGroupLevels } from "../../constants";

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function formatShortDate(dateString) {
  if (!dateString) return "";
  const iso = String(dateString).slice(0, 10);
  const [, month, day] = iso.split("-");
  return day && month ? `${day}.${month}` : iso;
}

function errorText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload.detail === "string") return payload.detail;
  if (Array.isArray(payload.detail) && payload.detail[0]?.msg) {
    return payload.detail[0].msg;
  }
  return fallback;
}

const ScheduleChkMore = ({
  token,
  restaurants = [],
  employees = [],
  currentRestaurantId,
  weekDates = [],
  accessLevel,
}) => {
  const canAssign = MngGroupLevels.includes(Number(accessLevel));
  const canDownload = Number(accessLevel) >= MNGLevel;

  const [restaurantId, setRestaurantId] = useState("");
  const [date, setDate] = useState(weekDates[0] || "");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignMessage, setAssignMessage] = useState(null);

  const [downloadStart, setDownloadStart] = useState(weekDates[0] || "");
  const [downloadEnd, setDownloadEnd] = useState(weekDates.at(-1) || "");
  const [downloading, setDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState(null);

  const otherRestaurants = useMemo(() => {
    return (Array.isArray(restaurants) ? restaurants : [])
      .filter(
        (restaurant) =>
          Number(restaurant.id) !== Number(currentRestaurantId),
      )
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "ru"));
  }, [restaurants, currentRestaurantId]);

  const filteredEmployees = useMemo(() => {
    const needle = employeeQuery.trim().toLowerCase();
    const list = Array.isArray(employees) ? [...employees] : [];
    list.sort((a, b) =>
      `${a.surname} ${a.name}`.localeCompare(`${b.surname} ${b.name}`, "ru"),
    );
    if (!needle) return list;
    return list.filter((user) =>
      `${user.surname ?? ""} ${user.name ?? ""} ${user.post_name ?? ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [employees, employeeQuery]);

  useEffect(() => {
    if (!weekDates[0]) return;
    setDate(weekDates[0]);
    setDownloadStart(weekDates[0]);
    setDownloadEnd(weekDates.at(-1) || weekDates[0]);
  }, [weekDates]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!canAssign || !restaurantId || !date) {
        setTemplates([]);
        setTemplateId("");
        return;
      }
      setTemplatesLoading(true);
      setAssignMessage(null);
      try {
        const data = await FreeTS(token, Number(restaurantId), date);
        if (cancelled) return;
        if (!Array.isArray(data)) {
          setTemplates([]);
          setTemplateId("");
          setAssignMessage({
            ok: false,
            text: errorText(data, "Не удалось загрузить свободные слоты"),
          });
          return;
        }
        setTemplates(data);
        setTemplateId("");
        if (data.length === 0) {
          setAssignMessage({
            ok: false,
            text: "На эту дату свободных слотов нет",
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setTemplates([]);
          setAssignMessage({
            ok: false,
            text: "Не удалось загрузить свободные слоты",
          });
        }
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, restaurantId, date, canAssign]);

  const saveForeignShift = async () => {
    if (!canAssign) return;
    if (!restaurantId) {
      setAssignMessage({ ok: false, text: "Выберите ресторан" });
      return;
    }
    if (!date) {
      setAssignMessage({ ok: false, text: "Выберите дату" });
      return;
    }
    if (!Number(employeeId)) {
      setAssignMessage({ ok: false, text: "Выберите сотрудника" });
      return;
    }
    if (!Number(templateId)) {
      setAssignMessage({ ok: false, text: "Выберите свободный слот" });
      return;
    }
    setAssignSaving(true);
    setAssignMessage(null);
    try {
      const result = await AddForeignShift(
        token,
        Number(templateId),
        Number(employeeId),
      );
      if (result?.detail) {
        setAssignMessage({
          ok: false,
          text: errorText(result, "Не удалось записать смену"),
        });
        return;
      }
      setAssignMessage({ ok: true, text: "Сотрудник записан на смену" });
      setTemplateId("");
      const data = await FreeTS(token, Number(restaurantId), date);
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAssignMessage({ ok: false, text: "Не удалось записать смену" });
    } finally {
      setAssignSaving(false);
    }
  };

  const fillCurrentWeek = () => {
    if (!weekDates[0]) return;
    setDownloadStart(weekDates[0]);
    setDownloadEnd(weekDates.at(-1) || weekDates[0]);
    setDownloadMessage(null);
  };

  const downloadReport = async () => {
    if (!canDownload) return;
    if (!downloadStart || !downloadEnd) {
      setDownloadMessage({ ok: false, text: "Выберите период" });
      return;
    }
    if (downloadStart > downloadEnd) {
      setDownloadMessage({ ok: false, text: "Начало периода позже конца" });
      return;
    }
    setDownloading(true);
    setDownloadMessage(null);
    try {
      const response = await GetReportCHK(token, downloadStart, downloadEnd);
      const blob = response?.data;
      if (!(blob instanceof Blob) || blob.size === 0) {
        setDownloadMessage({ ok: false, text: "Не удалось скачать отчёт" });
        return;
      }
      if (blob.type && blob.type.includes("json")) {
        try {
          const payload = JSON.parse(await blob.text());
          setDownloadMessage({
            ok: false,
            text: errorText(payload, "Нет доступа к отчёту"),
          });
        } catch (err) {
          console.error(err);
          setDownloadMessage({ ok: false, text: "Не удалось скачать отчёт" });
        }
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers?.["content-disposition"];
      let filename = `расписание_чк_${downloadStart}_${downloadEnd}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (match?.[1]) filename = match[1];
      }
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadMessage({ ok: true, text: "Файл скачан" });
    } catch (err) {
      console.error(err);
      setDownloadMessage({ ok: false, text: "Не удалось скачать отчёт" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="chk-more">
      <section className="chk-more-card chk-more-assign">
        <div className="chk-more-card-head">
          <h4>Смена в другом ресторане</h4>
          <p>
            Сотрудника своего ресторана ставим на свободный слот чужого графика.
          </p>
        </div>

        {!canAssign ? (
          <p className="chk-more-readonly">
            Записывать в другой ресторан могут MNG и директор.
          </p>
        ) : (
          <>
            <div className="chk-more-step">
              <span className="chk-more-step-num">1</span>
              <div className="chk-more-step-body">
                <strong>Куда</strong>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="chk-more-restaurant">
                      Ресторан
                    </label>
                    <select
                      id="chk-more-restaurant"
                      className="form-select"
                      value={restaurantId}
                      onChange={(event) => setRestaurantId(event.target.value)}
                    >
                      <option value="">Не выбран</option>
                      {otherRestaurants.map((restaurant) => (
                        <option key={restaurant.id} value={restaurant.id}>
                          {restaurant.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="chk-more-date">
                      Дата
                    </label>
                    <input
                      id="chk-more-date"
                      className="form-control"
                      type="date"
                      value={date}
                      onChange={(event) => setDate(event.target.value)}
                    />
                  </div>
                </div>
                {weekDates.length === 7 && (
                  <div className="chk-more-days">
                    {weekDates.map((weekDate, index) => (
                      <button
                        key={weekDate}
                        type="button"
                        className={`chk-more-day ${
                          date === weekDate ? "is-active" : ""
                        }`}
                        onClick={() => setDate(weekDate)}
                      >
                        {DAY_LABELS[index]}
                        <span>{formatShortDate(weekDate)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="chk-more-step">
              <span className="chk-more-step-num">2</span>
              <div className="chk-more-step-body">
                <strong>Кого и на какой слот</strong>
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="chk-more-employee-search">
                      Сотрудник
                    </label>
                    <input
                      id="chk-more-employee-search"
                      type="search"
                      className="form-control mb-1"
                      placeholder="Поиск по ФИО"
                      value={employeeQuery}
                      onChange={(event) => setEmployeeQuery(event.target.value)}
                    />
                    <select
                      id="chk-more-employee"
                      className="form-select"
                      value={employeeId}
                      onChange={(event) => setEmployeeId(event.target.value)}
                    >
                      <option value="">Не выбран</option>
                      {filteredEmployees.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.surname} {user.name}
                          {user.post_name ? ` · ${user.post_name}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label" htmlFor="chk-more-template">
                      Свободный слот
                    </label>
                    <select
                      id="chk-more-template"
                      className="form-select"
                      value={templateId}
                      disabled={!restaurantId || !date || templatesLoading}
                      onChange={(event) => setTemplateId(event.target.value)}
                    >
                      <option value="">
                        {templatesLoading
                          ? "Загрузка…"
                          : !restaurantId || !date
                            ? "Сначала ресторан и дата"
                            : templates.length
                              ? "Не выбран"
                              : "Свободных слотов нет"}
                      </option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.start}–{template.end} · {template.hours} ч ·{" "}
                          {template.station_name || "Без станции"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="chk-more-card-footer">
              {assignMessage ? (
                <span className={assignMessage.ok ? "is-ok" : "is-error"}>
                  {assignMessage.text}
                </span>
              ) : (
                <span className="chk-more-footer-hint">
                  Слоты подгружаются сами после выбора ресторана и даты
                </span>
              )}
              <button
                type="button"
                className="btn"
                onClick={saveForeignShift}
                disabled={assignSaving || templatesLoading}
              >
                {assignSaving ? "Запись…" : "Записать"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="chk-more-card chk-more-download">
        <div className="chk-more-card-head">
          <h4>Скачать расписание</h4>
          <p>Excel по своему ресторану за выбранный период.</p>
        </div>

        {!canDownload ? (
          <p className="chk-more-readonly">Скачивание доступно с уровня MNG.</p>
        ) : (
          <>
            <div className="chk-more-download-fields">
              <div className="chk-more-download-field">
                <label className="form-label" htmlFor="chk-more-download-start">
                  Начало
                </label>
                <input
                  id="chk-more-download-start"
                  type="date"
                  className="form-control"
                  value={downloadStart}
                  onChange={(event) => setDownloadStart(event.target.value)}
                />
              </div>
              <div className="chk-more-download-field">
                <label className="form-label" htmlFor="chk-more-download-end">
                  Конец
                </label>
                <input
                  id="chk-more-download-end"
                  type="date"
                  className="form-control"
                  value={downloadEnd}
                  onChange={(event) => setDownloadEnd(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="schedule-chk-week-tool-btn chk-more-download-week"
                onClick={fillCurrentWeek}
              >
                Текущая неделя
              </button>
            </div>
            <div className="chk-more-card-footer">
              {downloadMessage ? (
                <span className={downloadMessage.ok ? "is-ok" : "is-error"}>
                  {downloadMessage.text}
                </span>
              ) : (
                <span className="chk-more-footer-hint">
                  {weekDates[0]
                    ? `Сейчас выбрана неделя ${formatShortDate(weekDates[0])} — ${formatShortDate(weekDates.at(-1))}`
                    : "Выберите период"}
                </span>
              )}
              <button
                type="button"
                className="btn"
                onClick={downloadReport}
                disabled={downloading}
              >
                {downloading ? "Скачивание…" : "Скачать xlsx"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default ScheduleChkMore;
